import { useUnseenMessages } from '@application/api';
import type { Route } from '@application/types';
import { useRouter } from '@application/utils';
import type { MtLayoutNavbarLink, MtLayoutNavbarLinkCategory } from '@presentation/components';
import { useCallback, useMemo } from 'react';
import { useCurrentProfileStep } from '../../pages/MtBreederOnboardingPageController/api/useCurrentProfileStep';

export const useNavbarLinkCategoriesBreeder = (): MtLayoutNavbarLinkCategory[] => {
  const { navigateToRoute, infoFromCurrentPath, translate } = useRouter();

  const { profileValidated, profileLinksLocked, currentProfileProgress, profileLoading } =
    useCurrentProfileStep();

  const { unseenMessagesAmount } = useUnseenMessages();

  const locked = !profileValidated;
  const littersAnimalsLocked = profileLoading ? false : currentProfileProgress === 1;

  const navbarLinkRoute = useCallback(
    (route: Route): Partial<MtLayoutNavbarLink> => ({
      active: infoFromCurrentPath.route === route,
      onClick: () => navigateToRoute(route),
    }),
    [infoFromCurrentPath.route, navigateToRoute],
  );

  const verifiedBreederTooltip = useMemo(() => translate('mt.header.verifiedBreeder'), [translate]);

  return [
    {
      label: translate('mt.header.profileTitle'),
      links: [
        {
          ...navbarLinkRoute('mtLanding'),
          label: translate('mt.header.home'),
          icon: 'home',
        },
        {
          ...navbarLinkRoute('mtBreederProfile'),
          label: translate('mt.header.breederProfile'),
          icon: profileLinksLocked ? 'lock' : 'user',
          disabled: profileLinksLocked,
          tooltip: profileLinksLocked ? verifiedBreederTooltip : '',
        },
        {
          ...navbarLinkRoute('mtLitters'),
          label: translate('mt.header.litters'),
          icon: littersAnimalsLocked ? 'lock' : 'paw',
          disabled: littersAnimalsLocked,
          tooltip: littersAnimalsLocked ? verifiedBreederTooltip : '',
        },
        {
          ...navbarLinkRoute('mtAnimals'),
          label: translate('mt.header.parents'),
          icon: littersAnimalsLocked ? 'lock' : 'dog',
          disabled: littersAnimalsLocked,
          tooltip: littersAnimalsLocked ? verifiedBreederTooltip : '',
        },
      ],
    },
    {
      label: translate('mt.header.applicationsTitle'),
      links: [
        {
          ...navbarLinkRoute('mtApplications'),
          tooltip: locked ? verifiedBreederTooltip : '',
          disabled: locked,
          label: translate('mt.header.applications'),
          icon: locked ? 'lock' : 'list',
        },
        {
          ...navbarLinkRoute('mtWaitingList'),
          tooltip: locked ? verifiedBreederTooltip : '',
          disabled: locked,
          label: translate('mt.header.waitingList'),
          icon: locked ? 'lock' : 'hourglass-half',
        },
        {
          ...navbarLinkRoute('mtOwners'),
          tooltip: locked ? verifiedBreederTooltip : '',
          disabled: locked,
          label: translate('mt.header.owners'),
          icon: locked ? 'lock' : 'house-user',
        },
        {
          ...navbarLinkRoute('mtMessages'),
          label: translate('mt.header.messages'),
          icon: locked ? 'lock' : 'comment',
          tooltip: locked ? verifiedBreederTooltip : '',
          disabled: locked,
          notifications: unseenMessagesAmount,
        },
      ],
    },
    {
      bottomPinned: true,
      links: [
        {
          ...navbarLinkRoute('mtAdvantages'),
          label: translate('mt.header.advantages'),
          icon: 'gift',
          highlight: true,
        },
      ],
    },
    {
      bottomPinned: true,
      links: [
        {
          ...navbarLinkRoute('mtSettings'),
          label: translate('mt.header.settings'),
          icon: 'cog',
        },
        {
          ...navbarLinkRoute('mtFaq'),
          label: translate('mt.header.faq'),
          icon: 'question',
        },
        {
          ...navbarLinkRoute('mtContact'),
          label: translate('mt.header.contact'),
          icon: 'question-circle',
        },
      ],
    },
  ];
};
